import React from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { ZReportModel } from "../models/pos"
import { useTranslation } from "react-i18next"

function formatMoney(minor: number | undefined, currency = "EUR") {
    const value = (Number(minor || 0) / 100).toFixed(2)
    return `${value} ${currency}`
}

function formatDateTime(value: string | undefined, locale: string) {
    return value
        ? new Date(value).toLocaleString(locale, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "—"
}

function formatDirection(
    direction: string | undefined,
    translate: (key: string) => string
) {
    const normalized = String(direction || "").toLowerCase()
    if (normalized === "in")
        return translate("zReport.cashMovements.direction.in")
    if (normalized === "out")
        return translate("zReport.cashMovements.direction.out")
    return direction || "—"
}

type ZReportViewProps = {
    open: boolean
    onClose: () => void
    report?: ZReportModel | null
}

function ZReportView({ open, onClose, report }: ZReportViewProps) {
    const { t, i18n } = useTranslation()

    if (!report) return null

    const { session, sales, paymentMethods, vatBreakdown, cashMovements } =
        report
    const locale = i18n.language || "fr-FR"
    const openedAt = formatDateTime(session.openedAt, locale)
    const closedAt = formatDateTime(session.closedAt, locale)
    const sessionLabel = String(session.ticketZNumber || "").trim()
        ? `Ticket Z — ${String(session.ticketZNumber)}`
        : t("zReport.title", {
              id: String(session.id || "").substring(0, 8),
          })

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    m: { xs: 1, sm: 2 },
                },
            }}
        >
            <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                {sessionLabel}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t("zReport.session.title")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.session.company")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {String(session.companyName || "—")}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.session.outlet")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {String(session.outletName || "—")}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                SIRET
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {String(session.companySiret || "—")}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Caisse
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {String(session.registerName || "—")}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.session.status")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {String(session.status || "").toUpperCase()}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.session.openedAt")}
                            </Typography>
                            <Typography variant="body1">{openedAt}</Typography>
                        </Grid>
                        {session.closedAt && (
                            <Grid item xs={6}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t("zReport.session.closedAt")}
                                </Typography>
                                <Typography variant="body1">
                                    {closedAt}
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.session.openingFloat")}
                            </Typography>
                            <Typography variant="body1">
                                {formatMoney(
                                    session.openingFloatMinor,
                                    session.currency
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t("zReport.sales.title")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.sales.transactionCount")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {sales.total_sales || 0}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("zReport.sales.revenue")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                            >
                                {formatMoney(
                                    sales.total_revenue_minor,
                                    sales.currency
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t("zReport.paymentMethods.title")}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t("zReport.paymentMethods.mode")}
                                </TableCell>
                                <TableCell>
                                    {t("zReport.paymentMethods.type")}
                                </TableCell>
                                <TableCell>Ref. Ticket</TableCell>
                                <TableCell align="right">
                                    {t(
                                        "zReport.paymentMethods.transactionCount"
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.net")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.vat")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.gross")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paymentMethods.map((pm, idx: number) => (
                                <TableRow key={idx}>
                                    <TableCell>{pm.label}</TableCell>
                                    <TableCell>{pm.type || "—"}</TableCell>
                                    <TableCell>
                                        {Array.isArray(pm.ticket_refs) &&
                                        pm.ticket_refs.length > 0
                                            ? pm.ticket_refs.join(", ")
                                            : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {pm.transaction_count}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            pm.net_total_minor,
                                            pm.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            pm.vat_total_minor,
                                            pm.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            pm.gross_total_minor,
                                            pm.currency
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {paymentMethods.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {t("zReport.paymentMethods.empty")}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t("zReport.vat.title")}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("zReport.vat.rate")}</TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.net")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.vat")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("zReport.common.gross")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vatBreakdown.map((vat, idx: number) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        {vat.label} (
                                        {(Number(vat.rate || 0) * 100).toFixed(
                                            2
                                        )}
                                        %)
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            vat.net_total_minor,
                                            vat.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            vat.vat_total_minor,
                                            vat.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMoney(
                                            vat.gross_total_minor,
                                            vat.currency
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {vatBreakdown.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        {t("zReport.vat.empty")}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>

                {cashMovements && cashMovements.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t("zReport.cashMovements.title")}
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {t("zReport.cashMovements.date")}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "zReport.cashMovements.direction.label"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t("zReport.cashMovements.reason")}
                                        </TableCell>
                                        <TableCell>
                                            {t("zReport.cashMovements.type")}
                                        </TableCell>
                                        <TableCell>
                                            {t("zReport.cashMovements.mode")}
                                        </TableCell>
                                        <TableCell>
                                            {t("zReport.cashMovements.note")}
                                        </TableCell>
                                        <TableCell>
                                            {t("zReport.cashMovements.user")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("zReport.cashMovements.amount")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cashMovements.map((cm, idx: number) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                {formatDateTime(
                                                    String(cm.created_at || ""),
                                                    locale
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatDirection(
                                                    String(cm.direction || ""),
                                                    t
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {cm.reason_code}
                                            </TableCell>
                                            <TableCell>
                                                {String(
                                                    cm.movement_type || "—"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {String(
                                                    cm.payment_method_type ||
                                                        "—"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {String(cm.note || "—")}
                                            </TableCell>
                                            <TableCell>
                                                {String(
                                                    cm.created_by_user_display_name ||
                                                        "—"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatMoney(
                                                    cm.amount_minor,
                                                    cm.currency
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}

                {session.status === "closed" && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t("zReport.counting.title")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("zReport.counting.expected")}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        {formatMoney(
                                            session.expectedCashMinor,
                                            session.currency
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("zReport.counting.counted")}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        {formatMoney(
                                            session.countedCashMinor,
                                            session.currency
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("zReport.counting.discrepancy")}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                        color={
                                            session.discrepancyMinor === 0
                                                ? "success.main"
                                                : "error.main"
                                        }
                                    >
                                        {formatMoney(
                                            session.discrepancyMinor,
                                            session.currency
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                <Button
                    onClick={onClose}
                    sx={{
                        minHeight: 48,
                        px: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                >
                    {t("common.close")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ZReportView
